import { useCallback } from 'react';
import amplitudeLog from '@client/amplitude';
import { AMP_ELEMENT_EXPAND } from '@shared/constants';
import { getId } from '@shared/helpers/object.helpers';
import {
  findElementAndParent,
  hasChildren,
  isElement,
} from '@shared/helpers/recursive-element.helpers';
import { OneOfElements, OneOfListElements } from '@shared/models/project.model';
import { ItemOrItemId } from '@shared/models/type-helpers.model';
import { createLocalStorageRecordStore } from '../helpers/local-storage.helpers';
import {
  getSelectedOrganization,
  useSelectedOrganizationName,
} from '@client/store/organization';
import { getProject } from '@client/store/project';
import { getSelectedVersion } from '@client/store/ui';
import { isOneOf } from '@shared/helpers/array.helpers';
import { isProjectInfoOrFolder } from '@shared/helpers/project-folder.helpers';
import { IProduct } from '@shared/models/product.model';
import { guards } from '@shared/helpers/typeguards.helpers';
import { clearSelectionForCollapsedDescendants } from './element-list-collapse.helpers';
import { useProjectNavigation } from './use-router.hooks';

/**
 * Names of panels in side panel
 */
const PANEL_NAMES = ['Properties', 'Project Area', 'Lifecycle', 'Energy'];

const { getItem, setItem, useStoreItem, useStoreRecord, getRecord, setRecord } =
  createLocalStorageRecordStore<string, boolean>('expanded_elements');

type ExpandItem = ItemOrItemId<OneOfListElements | IProduct>;

const isPanelName = (item: ExpandItem): boolean =>
  isOneOf(PANEL_NAMES, getId(item));

/**
 * Reusable function to get state key for expanded elements
 */
const getStoreKey = (
  elementOrId: ExpandItem,
  isProject = isProjectInfoOrFolder(elementOrId),
) => {
  const id = getId(elementOrId).toString(); // Project.id is a number
  const organization = getSelectedOrganization(true);
  const prjId = getProject().id;

  if (isProject && !isPanelName(id)) {
    return `${organization}_projects_${id}`;
  }
  return `${organization}_${String(prjId)}_${id}`;
};

export const isElementExpanded = (
  elementOrId: ExpandItem,
  isProject = isProjectInfoOrFolder(elementOrId),
) => getItem(getStoreKey(elementOrId, isProject)) ?? false;

const composeExpandedItemsStorageKey = (
  organization: string | undefined,
  projectId: number | undefined,
  prefix: string | undefined,
  id: string,
): string | undefined => {
  if (organization === undefined || projectId === undefined) return undefined;
  const parts = ['custom', organization, String(projectId)];
  if (prefix) parts.push(prefix);
  parts.push(id);
  return parts.join('::');
};

const getExpandedItemsKey = (prefix: string, id: string): string | undefined =>
  composeExpandedItemsStorageKey(
    getSelectedOrganization(),
    getProject()?.id,
    prefix,
    id,
  );

export const isGroupExpandedByKey = (groupKey: string): boolean =>
  isExpandedItemsByKey('sbef', groupKey);

export const setGroupExpandedByKey = (
  groupKey: string,
  expanded: boolean,
): void => setExpandedItemsByKey('sbef', groupKey, expanded);

export const isExpandedItemsByKey = (prefix: string, id: string): boolean => {
  const key = getExpandedItemsKey(prefix, id);
  if (!key) return false;
  return (getItem(key) ?? false) === true;
};

export const setExpandedItemsByKey = (
  prefix: string,
  id: string,
  expanded: boolean,
): void => {
  const key = getExpandedItemsKey(prefix, id);
  if (!key) return;
  setItem(key, expanded);
};

/**
 * Set if an element is expanded in the side panel
 * @param elementOrId
 * @param expanded
 * @param isProject
 * @param force Provide true if you want to force the expansion even if the element is not expandable (maybe empty right now but will have a children soon)
 */
export const setElementExpanded = (
  elementOrId: ExpandItem,
  expanded: boolean,
  isProject = isProjectInfoOrFolder(elementOrId),
  force?: boolean,
) => {
  setItem(
    getStoreKey(elementOrId, isProject),
    expanded && (isExpandAllowed(elementOrId) || !!force),
  );
};

export const toggleElementExpanded = (
  elementOrId: ExpandItem,
  isProject = isProjectInfoOrFolder(elementOrId),
  force?: boolean,
) => {
  const nextExpanded = !isElementExpanded(elementOrId, isProject);
  setElementExpanded(elementOrId, nextExpanded, isProject, force);
  if (!isProject) {
    amplitudeLog(AMP_ELEMENT_EXPAND, {
      ElementId: getId(elementOrId),
      expanded: nextExpanded,
    });
  }
};

export const useToggleElementExpanded = (
  element: OneOfListElements | IProduct,
) => {
  const navigateTo = useProjectNavigation();

  return useCallback(() => {
    const isProject = isProjectInfoOrFolder(element);
    if (isElementExpanded(element, isProject) && isElement(element)) {
      clearSelectionForCollapsedDescendants(element, navigateTo);
    }
    toggleElementExpanded(element, isProject);
  }, [element, navigateTo]);
};

export const useExpandParents = () => {
  return useCallback((elementOrId?: ItemOrItemId<OneOfElements>) => {
    const version = getSelectedVersion();
    if (!elementOrId || !version) {
      return;
    }
    const id = getId(elementOrId);
    const { path } = findElementAndParent(version, id);

    expandElements(...path.filter((e) => isElement(e)));
  }, []);
};

export const useIsElementExpanded = (elementOrId: ExpandItem): boolean => {
  const isPanel = isPanelName(elementOrId);
  const isProject = isProjectInfoOrFolder(elementOrId);
  const key = getStoreKey(elementOrId, isProject);
  const item = useStoreItem(key);
  if (!isExpandAllowed(elementOrId)) {
    return false;
  }
  return item ?? isPanel; // Panels are expanded by default
};

export const expandElements = (...items: ExpandItem[]): void => {
  const currentRecord = getRecord() ?? {};
  const nextRecord = { ...currentRecord };
  let hasChanges = false;

  items.forEach((item) => {
    const key = getStoreKey(item, isProjectInfoOrFolder(item));
    if (currentRecord[key] === true) return;

    const nextValue = isExpandAllowed(item);
    if (currentRecord[key] === nextValue) return;

    nextRecord[key] = nextValue;
    hasChanges = true;
  });

  if (hasChanges) {
    setRecord(nextRecord);
  }
};

export const collapseElements = (...items: ExpandItem[]): void => {
  const currentRecord = getRecord() ?? {};
  const nextRecord = { ...currentRecord };
  let hasChanges = false;

  items.forEach((item) => {
    const key = getStoreKey(item, isProjectInfoOrFolder(item));
    if (currentRecord[key] === false) return;
    nextRecord[key] = false;
    hasChanges = true;
  });

  if (hasChanges) {
    setRecord(nextRecord);
  }
};

/**
 * Check if element can be expanded. Currently only checks IElement kind, rest are allowed
 * @param element
 * @returns
 */
const isExpandAllowed = (element: ExpandItem): boolean =>
  isElement(element) ? hasChildren(element) : true;

export type UseExpandedItemOptions = {
  defaultValue?: boolean;
  defaultPrefix?: string;
};

export const useExpandedItems = (
  options?: UseExpandedItemOptions,
): Readonly<{
  is: (key: string) => boolean;
  set: (key: string, next: boolean | ((prev: boolean) => boolean)) => void;
}> => {
  const organization = useSelectedOrganizationName();
  const projectId = getProject()?.id;
  const prefix = options?.defaultPrefix;
  const defaultValue = options?.defaultValue ?? false;

  const compose = useCallback(
    (key: string): string | undefined =>
      composeExpandedItemsStorageKey(organization, projectId, prefix, key),
    [organization, projectId, prefix],
  );

  const record = useStoreRecord((state) => state ?? {});

  const is = useCallback(
    (key: string): boolean => {
      const fullKey = compose(key);
      if (!fullKey) return false;
      return (record?.[fullKey] ?? defaultValue) === true;
    },
    [record, compose, defaultValue],
  );

  const set = useCallback(
    (key: string, next: boolean | ((prev: boolean) => boolean)): void => {
      const fullKey = compose(key);
      if (!fullKey) return;
      const prev = getItem(fullKey) ?? defaultValue;
      const resolved = guards.is.boolean(next) ? next : next(prev);
      setItem(fullKey, resolved);
    },
    [defaultValue, compose],
  );

  return { is, set } as const;
};
